// Reading a plan, whole.
//
// ---------------------------------------------------------------------------
// REFETCH, NOT PATCH
// ---------------------------------------------------------------------------
//
// `docs/archived/09-web-client.md` puts "client-side merge of pushed content" on the list of
// things the implementer must not decide, and gives the reason in a sentence worth keeping
// in front of whoever changes this file: "every piece of client-side state is a place the
// client can disagree with the server about what a family said."
//
// So a signal produces a call to `loadSnapshot`, which reads the plan, the threads, the
// people, the decisions, the open actions and the messages, and REPLACES the whole
// snapshot. There is no reconciliation anywhere in this package, no message merge, no
// "insert if newer", no last-write-wins. The whole of the client's state is the last
// snapshot the server described.
//
// ---------------------------------------------------------------------------
// THE ONE THING THAT SURVIVES A REFETCH: THE DEPTH
// ---------------------------------------------------------------------------
//
// A reader who has paged back through six months of conversation and then receives a
// signal must not be yanked to the bottom. The obvious fix is to merge the new page into
// the pages already held, which is exactly the merge logic that is forbidden.
//
// What is held instead is a NUMBER: how many pages deep the reader has asked to be. A
// refetch re-reads that many pages from the server in one pass, following the server's own
// `nextBefore` cursors, and replaces everything. No page is merged with an older copy of
// itself, no cursor is computed here, and the concatenation is contiguous because each
// page is defined by the cursor the previous page returned. New messages arriving between
// two refetches shift where the page boundaries fall and cannot duplicate or drop a
// message, which is acceptance case 3.
//
// ---------------------------------------------------------------------------
// WHICH THREAD
// ---------------------------------------------------------------------------
//
// `Conversation` shows the group thread the moment one exists, and the reader's own thread
// before then — `mainThreadOf` below, which is `kind`, not recency. D77 keeps the
// principal's preparing thread open through the move rather than closing it, and mints
// each invitee's private thread at redemption, strictly after the shared thread already
// exists; a client that picked "whichever thread is newest" would show every invitee their
// own private thread — the one `Private chat` already shows independently — and never the
// group. Reading `plan.state` instead would be the same bug in a different shape: a client
// that inferred the thread from the plan's state would be one bug away from showing a
// child the preparing conversation.
//
// If the group thread is closed, there is no composer and it is shown read-only. That is
// the `done` plan, and it is not an error.

import type { ApiClient } from '../api/client.js';
import type {
  Decision,
  Message,
  PendingAction,
  Person,
  Plan,
  Objective,
  Thread,
} from '../api/contract.js';

export type PlanSnapshot = {
  readonly plan: Plan;
  readonly threads: readonly Thread[];
  /** The thread being read, or null when this caller participates in none. */
  readonly thread: Thread | null;
  readonly messages: readonly Message[];
  readonly people: readonly Person[];
  readonly decisions: readonly Decision[];
  readonly objectives: readonly Objective[];
  /**
   * Every open action this reader can see, which is every open action they may answer.
   *
   * Not filtered here, and not filterable here: the server serves the list already narrowed
   * to conversations the reader is in, so there is no rule in this package about who sees
   * what to get wrong or to drift.
   */
  readonly pendingActions: readonly PendingAction[];
  /** The server's cursor for the next older page, or null at the beginning of time. */
  readonly nextBefore: string | null;
  /** How many pages were read. Carried so the next refetch reads the same depth. */
  readonly depth: number;
};

export type LoadSnapshot = (
  client: ApiClient,
  planId: string,
  depth: number,
) => Promise<PlanSnapshot>;

/** The newest thread still accepting messages, else the newest thread, else null. */
export function chooseThread(threads: readonly Thread[]): Thread | null {
  const ordered = [...threads].sort((a, b) => compare(a, b));
  const open = ordered.filter((thread) => thread.acceptingMessages);
  return open[open.length - 1] ?? ordered[ordered.length - 1] ?? null;
}

/**
 * The one group conversation, or null before the plan has moved to deciding.
 *
 * `kind === 'shared'` rather than `plan.state`, for the same reason `chooseThread` never
 * read `plan.state` either: the thread the server actually put this caller in is the fact,
 * not an inference from the plan's state.
 */
export function groupThreadOf(threads: readonly Thread[]): Thread | null {
  return threads.find((thread) => thread.kind === 'shared') ?? null;
}

export function principalsThreadOf(threads: readonly Thread[]): Thread | null {
  return threads.find((thread) => thread.kind === 'principals') ?? null;
}

/**
 * The reader's own 1:1 with the facilitator — their `preparing` thread if they are the
 * principal from before the group arrived, their `private` thread if they redeemed an
 * invitation (D77). The API returns only threads this caller participates in, and a solo
 * thread is by construction theirs alone, so "not the shared thread" is enough to find it
 * without caring which of the two kinds it is.
 */
export function ownThreadOf(threads: readonly Thread[]): Thread | null {
  return (
    threads.find((thread) => thread.kind !== 'shared' && thread.kind !== 'principals') ??
    null
  );
}

/**
 * The thread `Conversation` reads: the group thread whenever one exists, the reader's own
 * thread before then.
 *
 * NOT `chooseThread` (newest-wins): redemption creates an invitee's `private` thread after
 * the plan's `shared` thread already exists, so for every invitee the shared thread is
 * older than their own, not newer. A recency rule would hand `Conversation` the private
 * thread — the one `Private chat` already shows independently — and never the group at
 * all.
 */
export function mainThreadOf(threads: readonly Thread[]): Thread | null {
  return groupThreadOf(threads) ?? principalsThreadOf(threads) ?? ownThreadOf(threads);
}

function compare(a: Thread, b: Thread): number {
  if (a.createdAt !== b.createdAt) {
    return a.createdAt < b.createdAt ? -1 : 1;
  }
  return a.id < b.id ? -1 : 1;
}

export type MessagePages = {
  readonly messages: readonly Message[];
  readonly nextBefore: string | null;
  readonly depth: number;
};

/**
 * One thread's messages, `depth` pages of them, oldest first.
 *
 * Pulled out of `loadSnapshot` so `usePrivateThread` can read a second thread the same
 * way — same pagination, same "no merge, replace the lot" discipline — without a second
 * copy of this loop.
 */
export async function readMessagePages(
  client: ApiClient,
  threadId: string,
  depth: number,
): Promise<MessagePages> {
  const pages = Math.max(1, depth);
  // Oldest-first at the end, so the pages are read newest-first and reversed once.
  const collected: Message[][] = [];
  let cursor: string | null = null;
  let read = 0;
  for (; read < pages; read += 1) {
    const page: { messages: readonly Message[]; nextBefore: string | null } =
      await client.readMessages(threadId, cursor);
    collected.push([...page.messages]);
    cursor = page.nextBefore;
    if (cursor === null) {
      read += 1;
      break;
    }
  }
  collected.reverse();
  return {
    messages: Object.freeze(collected.flat()),
    nextBefore: cursor,
    depth: Math.max(1, read),
  };
}

export const loadSnapshot: LoadSnapshot = async (client, planId, depth) => {
  const [plan, threads, people, decisions, objectives, pendingActions] =
    await Promise.all([
      client.readPlan(planId),
      client.listThreads(planId),
      client.listPeople(planId),
      client.listDecisions(planId),
      client.listObjectives(planId),
      client.listPendingActions(planId),
    ]);

  const thread = mainThreadOf(threads);
  if (thread === null) {
    return Object.freeze({
      plan,
      threads,
      thread: null,
      messages: Object.freeze([]),
      people,
      decisions,
      objectives,
      pendingActions,
      nextBefore: null,
      depth: Math.max(1, depth),
    });
  }

  const pages = await readMessagePages(client, thread.id, depth);

  return Object.freeze({
    plan,
    threads,
    thread,
    messages: pages.messages,
    people,
    decisions,
    objectives,
    pendingActions,
    nextBefore: pages.nextBefore,
    depth: pages.depth,
  });
};

/**
 * What the local list looks like after a message is posted, before the refetch lands.
 *
 * THE ONE PIECE OF OPTIMISTIC STATE THIS CLIENT HOLDS, and it is not optimistic in the
 * usual sense: what gets inserted is the message the SERVER returned from the POST, with
 * the server's `seq`, the server's `createdAt` and the server's `authorId`. Nothing here
 * constructs any of them. A client that built a placeholder would be inventing a sequence
 * number and a timestamp, both of which the design forbids by name, and would then have to
 * reconcile the placeholder with the real row — the merge logic that is forbidden
 * separately.
 *
 * Insertion is by id, so the refetch that follows a moment later replaces this list with
 * the server's and produces no duplicate whether it arrives before or after the signal.
 * Acceptance case 3 is the test of exactly this pair of lines.
 */
export type AfterPost = (
  messages: readonly Message[],
  posted: Message,
) => readonly Message[];

export const afterPost: AfterPost = (messages, posted) => {
  if (messages.some((message) => message.id === posted.id)) {
    return messages;
  }
  return Object.freeze([...messages, posted]);
};
